<template>
  <div class="">
    <div v-if="!isLogoLoading" class="is-flex is-align-items-center is-justify-content-center">
      <img v-if="isWhiteLabeled && logoUrl && logoExists" :src="logoUrl" alt="Platform Logo" class="px-4" />
      <img v-else-if="!isWhiteLabeled" :src="logoDefault" alt="Platform Logo" class="px-4" />
    </div>
    <card-component class="has-background-transparent" style="box-shadow: none" transparent>
      <p v-if="bodyText">{{ bodyText }}</p>
      <form @submit.prevent="submit">
        <ValidationObserver ref="loginObserver">
          <ValidationProvider v-if="hasField('email')" v-slot="{ errors }" rules="required">
            <b-field
              v-if="hasField('email')"
              :message="{
                'Please enter your email address': errors.length > 0,
              }"
              :type="{ 'is-danger': errors.length > 0 }"
              label="E-mail Address">
              <b-input
                v-model="form.email"
                :autofocus="!$store.getters['size/isMobile'] && state !== 'password'"
                custom-class="has-background-white"
                name="email"
                type="email"
                data-cy-login="Email Address"
                :disabled="disabledFields['email']" />
            </b-field>
          </ValidationProvider>

          <ValidationProvider v-if="hasField('tenants')" v-slot="{ errors }" rules="required">
            <b-field
              v-if="hasField('tenants')"
              :message="{
                'Select a School': errors.length > 0,
              }"
              :type="{ 'is-danger': errors.length > 0 }"
              label="Schools">
              <SelectTenant
                :options="data['tenants']"
                :value="form.tenant_id"
                @input="form.tenant_id = $event"
                :disabled="disabledFields['tenants']"
                data-cy-login="Select Tenant" />
            </b-field>
          </ValidationProvider>

          <ValidationProvider v-if="hasField('password')" v-slot="{ errors }" rules="required">
            <b-field
              v-if="hasField('password')"
              :message="{ 'Please enter your password': errors.length > 0 }"
              :type="{ 'is-danger': errors.length > 0 }"
              label="Password">
              <b-input
                v-model="form.password"
                custom-class="has-background-white"
                name="password"
                type="password"
                :disabled="disabledFields['password']"
                data-cy-login="Password"
                ref="password" />
            </b-field>
          </ValidationProvider>
        </ValidationObserver>
        <ValidationObserver v-if="hasField('password-create')" ref="observer">
          <b-field label="Password">
            <ValidationProvider v-slot="{ errors }" rules="required|min|password:@confirm">
              <b-input
                v-model="form.password"
                name="password"
                placeholder="password"
                required
                type="password"
                data-cy-login="Password" />
              <span>{{ errors[0] }}</span>
            </ValidationProvider>
          </b-field>

          <b-field label="Confirm password">
            <ValidationProvider v-slot="{ errors }" name="confirm" rules="required">
              <b-input
                v-model="form.password_confirmation"
                placeholder="confirm password"
                required
                type="password"
                data-cy-login="Confirm Password" />
              <span>{{ errors[0] }}</span>
            </ValidationProvider>
          </b-field>
        </ValidationObserver>
        <b-message type="is-danger" v-if="error" has-icon data-cy-login="Error Message">
          {{ error }}
        </b-message>
        <hr />
        <b-field>
          <b-button
            :class="{ 'is-loading': isLoading }"
            expanded
            native-type="submit"
            type="is-primary"
            data-cy-login="Submit Button">
            {{ submitText }}
          </b-button>
        </b-field>
        <b-field v-if="hasField('forgot password')">
          <b-button
            expanded
            native-type="button"
            type="is-light"
            data-cy-login="Forgot Password"
            @click.prevent="forgotPasswordClick"
            >Reset password
          </b-button>
        </b-field>
        <b-field v-if="hasField('help')">
          <b-button expanded native-type="button" type="is-light" data-cy-login="Help" @click.prevent="help"
            >{{ helpText }}
          </b-button>
        </b-field>
        <b-field v-if="hasField('reset')">
          <b-button expanded native-type="button" type="is-danger" data-cy-login="Restart" @click.prevent="reset">
            Restart
          </b-button>
        </b-field>
        <b-field v-if="hasField('go back')">
          <b-button
            expanded
            native-type="button"
            outlined
            type="is-link"
            data-cy-login="Go Back"
            @click.prevent="goBack"
            >Return to login
          </b-button>
        </b-field>
      </form>
    </card-component>
  </div>
</template>

<script>
  import CardComponent from "../layout/CardComponent";
  import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
  import { required } from "vee-validate/dist/rules";
  import SelectTenant from "@/components/global/selects/sis/SelectTenant.vue";

  extend("required", {
    ...required,
    message: "This field is required",
  });
  extend("password", {
    params: ["target"],
    validate(value, { target }) {
      return value === target;
    },
    message: "Password confirmation does not match",
  });
  extend("min", value => {
    if (value.length >= 8) {
      return true;
    }
    return "The password must be at least 8 characters";
  });
  export default {
    name: "LoginForm",
    components: {
      SelectTenant,
      CardComponent,
      ValidationObserver,
      ValidationProvider,
    },
    data() {
      return {
        isLogoLoading: false,
        logoExists: false,
        form: {
          email: this.$route.query.email || null,
          password: null,
          password_confirmation: null,
          tenant_id: null,
        },
      };
    },
    props: {
      isWhiteLabeled: {
        type: Boolean,
        default() {
          return false;
        },
      },
      bodyText: {
        type: String,
        default() {
          return null;
        },
      },
      submitText: {
        type: String,
        default() {
          return "Login";
        },
      },
      helpText: {
        type: String,
        default() {
          return "Help";
        },
      },
      fields: {
        type: Array,
        default() {
          return ["email", "password", "forgot password"];
        },
      },
      logoUrl: {
        type: String,
        required: false,
      },
      logoDefault: {
        type: String,
        default() {
          return "The platform logo";
        },
      },
      isLoading: {
        type: Boolean,
        default() {
          return false;
        },
      },
      data: {
        type: Object,
        default() {
          return {};
        },
      },
      error: {
        type: String,
        default() {
          return null;
        },
      },
      state: {
        type: String,
        default() {
          return null;
        },
      },
    },
    methods: {
      checkLogoExists(url) {
        if (!this.isWhiteLabeled) return false;
        return new Promise(resolve => {
          let img = new Image();
          img.onload = () => {
            resolve(true);
          };
          img.onerror = () => {
            resolve(false);
          };
          img.src = url;
        });
      },
      help() {
        this.$emit("help");
      },
      goBack() {
        let slug = this.$cookies.get("tenant_slug") || null;
        let is_white_labeled = this.$cookies.get("tenant_is_white_labeled") || null;
        if (slug && is_white_labeled) {
          this.$router.push("/login/" + slug);
        } else {
          this.$router.push("/login");
        }
      },
      hasField(field) {
        return this.fieldNames.includes(field);
      },
      forgotPasswordClick() {
        this.$emit("forgotPasswordClick", this.form);
      },
      registerClick() {
        this.$emit("registerClick");
      },
      reset() {
        this.form = {
          email: null,
          password: null,
          password_confirmation: null,
          tenant_id: null,
        };
        this.$emit("reset");
      },
      submit() {
        if (this.hasField("password-create")) {
          this.$refs.observer.validate().then(success => {
            if (!success) {
              return;
            }
            this.$emit("submitted", this.form);
          });
        } else {
          this.$refs.loginObserver.validate().then(success => {
            if (!success) {
              return;
            }
            this.$emit("submitted", this.form);
          });
        }
      },
    },
    computed: {
      fieldNames() {
        return this.fields.map(field => {
          if (typeof field === "string") {
            return field;
          }
          return field.name;
        });
      },
      disabledFields() {
        return this.fields.reduce((obj, field) => {
          if (typeof field === "string") {
            return obj;
          }
          obj[field.name] = field.disabled;
          return obj;
        }, {});
      },
    },
    watch: {
      state() {
        if (this.state === "password") {
          this.$nextTick(() => {
            this.$refs.password.focus();
          });
        }
      },
      async logoUrl() {
        this.isLogoLoading = true;
        this.logoExists = await this.checkLogoExists(this.logoUrl);
        this.isLogoLoading = false;
      },
    },
    async mounted() {
      this.isLogoLoading = true;
      this.logoExists = await this.checkLogoExists(this.logoUrl);
      this.isLogoLoading = false;
    },
  };
</script>
